<script lang="ts" setup>
import { computed, PropType } from "vue";
import { Formatter, MimeType } from "@/util";
import { Tooltip } from "@/components";
import {
  useTopPagesPerDocumentQuery,
  MaDocumentStatistic,
  Interval,
} from "@/graphql/operations";

const props = defineProps({
  doc: {
    type: Object as PropType<MaDocumentStatistic>,
    required: true,
  },
});

const { fetching, data } = useTopPagesPerDocumentQuery({
  variables: {
    docId: props!.doc!.documentId!,
  },
});

const topPages = computed(() => data?.value?.maPageStatistics?.nodes);

const formatDuration = (duration: Interval) => {
  return Formatter.formatDuration({
    hours: duration.hours || 0,
    minutes: duration.minutes || 0,
    seconds: duration.seconds || 0,
  });
};

const parseCount = (value?: string | null) => parseInt(value ?? "0", 10) || 0;
const numShares = computed(() => parseCount(props.doc.numShares));
const numCurrentShares = computed(() => parseCount(props.doc.numCurrentShares));
const hasShares = computed(() => numShares.value > 0);
const showCurrentShares = computed(
  () => numShares.value !== numCurrentShares.value,
);
</script>

<template>
  <div class="doc_stats">
    <div class="card_title">
      <b>{{ doc.displayTitle }}</b>
      <span v-if="MimeType.isVideo(doc.mimeType!)" class="tag"> Video </span>
    </div>

    <div class="container">
      <div class="col">
        <div class="image">
          <img :src="doc.thumbnailDownloadUrl!" />
        </div>
      </div>

      <div v-if="hasShares" class="col">
        <table class="data_table">
          <tbody>
            <tr>
              <td>
                {{ $t("documentSpaceAnalytics.topDocuments.metrics.visits") }}:
              </td>
              <td class="right">
                <b>{{ doc.numVisits }}</b>
              </td>
            </tr>
            <tr>
              <td>
                {{ $t("documentSpaceAnalytics.topDocuments.metrics.shares") }}:
              </td>
              <td class="right">
                <span class="shares_value">
                  <b>{{ doc.numShares }}</b>
                  <span v-if="showCurrentShares" class="current_shares">
                    ({{
                      $t("documentAnalytics.currentShares", {
                        count: doc.numCurrentShares,
                      })
                    }})
                  </span>
                  <Tooltip
                    v-if="showCurrentShares"
                    class="left multiline shares_tooltip"
                    keyboard-accessible
                  >
                    <template v-slot>
                      <span class="material-symbols-outlined shares_info_icon">
                        info
                      </span>
                    </template>
                    <template #tooltip>
                      {{ $t("documentAnalytics.sharesTooltip") }}
                    </template>
                  </Tooltip>
                </span>
              </td>
            </tr>

            <template v-if="parseInt(doc.numVisits!, 10) > 0">
              <tr v-if="!MimeType.isVideo(doc.mimeType!)">
                <td>
                  {{
                    $t("documentSpaceAnalytics.topDocuments.metrics.downloads")
                  }}:
                </td>
                <td class="right">
                  <b>{{ doc.numDownloads }}</b>
                </td>
              </tr>
              <tr>
                <td>
                  &empty;
                  {{
                    $t("documentSpaceAnalytics.topDocuments.metrics.duration")
                  }}:
                </td>
                <td class="right" v-if="doc.averageDurationInDocumentPerVisit">
                  <b>{{
                    formatDuration(doc.averageDurationInDocumentPerVisit!)
                  }}</b>
                </td>
              </tr>
              <tr>
                <td>
                  {{
                    $t(
                      "documentSpaceAnalytics.topDocuments.metrics.maxDuration",
                    )
                  }}:
                </td>
                <td class="right" v-if="doc.maxDurationInDocumentAcrossVisits">
                  <b>{{
                    formatDuration(doc.maxDurationInDocumentAcrossVisits!)
                  }}</b>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <div v-if="hasShares" class="col">
        <table
          class="data_table"
          v-if="
            !MimeType.isVideo(doc.mimeType!) && parseInt(doc.numVisits!, 10) > 0
          "
        >
          <thead>
            <tr>
              <th>
                {{ $t("documentSpaceAnalytics.topPages.metrics.pageNumber") }}
              </th>
              <th>
                {{ $t("documentSpaceAnalytics.topPages.metrics.numVisits") }}
              </th>
              <th>
                &empty;
                {{
                  $t("documentSpaceAnalytics.topPages.metrics.averageDuration")
                }}
              </th>
              <th>
                {{ $t("documentSpaceAnalytics.topPages.metrics.maxDuration") }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="page in topPages" :key="page!.pageNumber!">
              <td>{{ page!.pageNumber }}</td>
              <td>{{ page!.numVisits }}</td>
              <td>
                {{ formatDuration(page!.averageDurationOnPagePerVisit!) }}
              </td>
              <td>
                {{ formatDuration(page!.maxDurationOnPageAcrossVisits!) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else class="no_data_display">
        {{ $t("documentAnalytics.noShares") }}
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.doc_stats {
  border-radius: 0.3em;
  border: 1px solid var(--classic-border-strong, #cecece);
  box-sizing: border-box;
  color: var(--classic-text, #4a4a4a);
  max-width: 100%;
  font-size: 0.9rem;
  background: var(--classic-surface, #fff);
  padding: 1rem 1.2rem 1rem 1.2rem;
  margin-bottom: 1em;
}

.doc_stats .container {
  display: flex;
  margin-top: 0.5em;
}

.doc_stats .col {
  min-width: 0;
  width: calc(46% - 6em);
}

.doc_stats .col .data_table {
  table-layout: fixed;
}

.doc_stats .col:first-child {
  width: 12em;
  margin-left: 0;
}

.doc_stats .col:last-child {
  margin-left: 5%;
}

.card .container {
  display: flex;
}

.doc_stats .card_title {
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.doc_stats .image {
  height: 8em;
}

.doc_stats .image img {
  height: auto;
  max-height: 8em;
  width: auto;
  max-width: 100%;
}

.doc_stats .info {
  margin-top: 0.5em;
  width: 100%;
  line-height: 1.3em;
}

.doc_stats .info span {
  width: 90px;
  display: inline-block;
}

.data_table {
  box-shadow: none;
}

.no_data_display {
  display: flex;
  align-items: center;
  justify-content: center;
  width: calc(100% - (46% - 24em));
  opacity: 0.8;
  color: var(--classic-text, #363636);
  font-style: italic;
}

table.data_table thead th,
table.data_table tbody td {
  line-height: 1.8;
}

table.data_table td.right {
  text-align: right;
}

.shares_value {
  align-items: center;
  display: inline-flex;
  gap: 0.25rem;
  white-space: nowrap;
}

.current_shares {
  color: var(--classic-text-secondary, #666);
  font-size: 0.9em;
}

.shares_info_icon {
  color: var(--classic-text-muted, #777);
  cursor: help;
  font-size: 1rem;
}

.shares_tooltip {
  line-height: 1;
}

.tag {
  background-color: var(--classic-tag-surface, #eaeaea);
  padding: 0.5em 0.8em;
  border-radius: 2em;
  margin-left: 0.3em;
  display: inline-block;
  font-size: 0.9em;
  line-height: initial;
}
</style>
